'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { trackEvent } from '@/lib/posthog';
import { TurnstileEvents } from '@/lib/analytics-events';

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const LOAD_TIMEOUT_MS = 10_000;

/** Whether Turnstile bot protection is configured for this environment. */
export const TURNSTILE_ENABLED = !!SITE_KEY;

interface TurnstileWidgetProps {
  /** Called with a valid Cloudflare token after successful verification. */
  onVerify: (token: string) => void;
  /** Called when the Turnstile challenge fails (network, domain mismatch, etc.). */
  onError?: () => void;
  /** Called when a previously-issued token expires. */
  onExpire?: () => void;
  /** Assign a reset function so parent forms can refresh the widget after failed submissions. */
  resetRef?: React.MutableRefObject<(() => void) | null>;
  className?: string;
}

export function TurnstileWidget({
  onVerify,
  onError,
  onExpire,
  resetRef,
  className,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const [widgetError, setWidgetError] = useState(false);

  const handleVerify = useCallback(
    (token: string) => {
      if (!mountedRef.current) return;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setWidgetError(false);
      trackEvent(TurnstileEvents.VERIFIED);
      onVerify(token);
    },
    [onVerify],
  );

  const handleError = useCallback(() => {
    if (!mountedRef.current) return;
    setWidgetError(true);
    trackEvent(TurnstileEvents.FAILED);
    onError?.();
  }, [onError]);

  const handleExpire = useCallback(() => {
    if (!mountedRef.current) return;
    trackEvent(TurnstileEvents.EXPIRED);
    onExpire?.();
  }, [onExpire]);

  const renderWidget = useCallback(() => {
    if (!SITE_KEY || !containerRef.current || !window.turnstile) return;

    // Remove previous widget before re-rendering (handles retry after error)
    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: handleVerify,
      'error-callback': handleError,
      'expired-callback': handleExpire,
      theme: 'auto',
      appearance: 'interaction-only',
      'refresh-expired': 'auto',
      retry: 'auto',
    });

    // Expose reset function to parent via ref
    if (resetRef) {
      resetRef.current = () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      };
    }
  }, [handleVerify, handleError, handleExpire, resetRef]);

  const handleRetry = useCallback(() => {
    setWidgetError(false);
    trackEvent(TurnstileEvents.RETRY);
    if (window.turnstile) {
      renderWidget();
    }
  }, [renderWidget]);

  useEffect(() => {
    mountedRef.current = true;

    // No site key — Turnstile is not configured for this environment.
    // Forms use TURNSTILE_ENABLED to skip the token gate entirely.
    if (!SITE_KEY) return;

    // If turnstile is already loaded (e.g. navigating between auth pages), render immediately
    if (window.turnstile && containerRef.current && !widgetIdRef.current) {
      renderWidget();
    }

    // If the Turnstile script never loads within the timeout, surface error to user.
    // The form remains submittable if TURNSTILE_ENABLED is used correctly in the
    // disabled check — Supabase will return a clear error if captcha is required.
    timeoutRef.current = setTimeout(() => {
      if (!widgetIdRef.current && mountedRef.current) {
        trackEvent(TurnstileEvents.TIMEOUT);
        setWidgetError(true);
        onError?.();
      }
    }, LOAD_TIMEOUT_MS);

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      if (resetRef) {
        resetRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Not configured — render nothing. Forms skip the token gate via TURNSTILE_ENABLED.
  if (!SITE_KEY) return null;

  return (
    <>
      <Script src={SCRIPT_URL} strategy="afterInteractive" onReady={renderWidget} />
      <div ref={containerRef} className={className} />
      {widgetError && (
        <div className="flex items-center gap-2 text-sm text-[#EF4444]">
          <span>Security check failed.</span>
          <button
            type="button"
            onClick={handleRetry}
            className="font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}
    </>
  );
}
